import React, { useEffect, useState } from 'react'

// Seconds remaining until a server-issued ISO timestamp, ticking once a second,
// clamped at 0. Display-only: client clocks skew, so reaching 0 here must never
// drive state — the server's 404/410 is the source of truth for actual expiry.
export const useCountdown = expiresAt => {
  const [remaining, setRemaining] = useState(() => secondsUntil(expiresAt))

  useEffect(() => {
    setRemaining(secondsUntil(expiresAt))
    if (!expiresAt) return
    const interval = setInterval(() => setRemaining(secondsUntil(expiresAt)), 1000)
    return () => clearInterval(interval)
  }, [expiresAt])

  return remaining
}

export const secondsUntil = (expiresAt, nowMs = Date.now()) => {
  if (!expiresAt) return null
  const target = new Date(expiresAt).getTime()
  if (isNaN(target)) return null
  return Math.max(0, Math.floor((target - nowMs) / 1000))
}

export const formatCountdown = seconds => {
  if (seconds === null) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const URGENT_SECONDS = 30

// How long a code lives when nobody tells us. The server owns the real window;
// this only scales the ring when a caller has not passed the lifetime through.
const ASSUMED_LIFETIME_SECONDS = 120

// One palette for every surface that shows this countdown — the Unity viewer
// and the caffeine editor window draw the same three bands with these values.
// A timer that looks different depending on which client you opened reads as a
// different feature.
export const COUNTDOWN_COLORS = {
  healthy: '#6BB582',
  warning: '#E6AD4D',
  critical: '#D33B3B'
}

// Colour by the FRACTION of the code's life left, not by a fixed number of
// seconds. Thirty seconds is nearly over for a two-minute code and barely
// started for a ten-minute one, so a hard threshold means something different
// every time the server changes its expiry policy.
export const countdownColor = fraction =>
  fraction > 0.5 ? COUNTDOWN_COLORS.healthy
    : fraction > 0.25 ? COUNTDOWN_COLORS.warning
      : COUNTDOWN_COLORS.critical

// Stroke as a fraction of the ring's diameter.
//
// Matches what the other surfaces draw. It is expressed as a ratio rather than
// a pixel width so the ring keeps its weight at any size — a stroke baked at
// one diameter reads as a hairline when drawn smaller, which is exactly how
// these drifted apart before.
const RING_WEIGHT = 0.179

export const CountdownRing = ({ fraction, size = 16, title }) => {
  const stroke = Math.max(1.5, size * RING_WEIGHT)
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const clamped = Math.max(0, Math.min(1, fraction))

  return (
    <svg
      className="ld-ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-hidden={title ? undefined : 'true'}
      aria-label={title}
    >
      {/* The unfilled remainder, so a nearly-drained ring still reads as a
          ring rather than as a stray tick mark. */}
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth={stroke}
      />
      <circle
        className="ld-ring__arc"
        cx={size / 2} cy={size / 2} r={radius}
        fill="none"
        stroke={countdownColor(clamped)}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped)}
        // Start at twelve o'clock and drain clockwise, the direction a
        // stopwatch face trains people to read as time being spent.
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  )
}

// "Code expires in m:ss" under a device-login code, with a ring that drains
// alongside it. Flips to "Code expired" at 0 (the owning view's server-driven
// expired state replaces it shortly after).
export const CodeCountdown = ({ expiresAt, className, lifetimeSeconds }) => {
  const remaining = useCountdown(expiresAt)
  if (remaining === null) return null

  const lifetime = lifetimeSeconds > 0 ? lifetimeSeconds : ASSUMED_LIFETIME_SECONDS
  const fraction = Math.max(0, Math.min(1, remaining / lifetime))

  // Keep the old fixed-seconds rule for the urgent CLASS so existing styling
  // and tests keep their meaning; the ring is what carries the finer banding.
  const urgent = remaining <= URGENT_SECONDS
  // Flex with a gap, like .ld-countdown. Without it the ring and its text
  // were two inline boxes with nothing between them, and the ring sat hard
  // against the "C".
  const fallback = `d-flex align-items-center justify-content-center gap-2 small ${urgent ? 'text-danger' : 'text-body-secondary'}`

  return (
    <div className={`${className || fallback}${className && urgent ? ' is-urgent' : ''}`} role="timer">
      {remaining > 0 && <CountdownRing fraction={fraction} />}
      {remaining === 0 ? 'Code expired' : `Code expires in ${formatCountdown(remaining)}`}
    </div>
  )
}
